/**
 * Program 12.1 UX4 — Bid bond operational panel.
 */
import React, { useState } from 'react';
import { Button, Card, InputNumber, Select, Space, Tag, Typography, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatMoney } from '@/utils/format';
import {
  BID_BOND_STATUS_OPTIONS,
  buildBidBondReadiness,
  parseBidBond,
  type BidBondStatus,
} from './bidBondTracker';

const { Text, Title } = Typography;

export type EpcBidBondPanelProps = {
  tenderId: string;
  tender: Record<string, unknown>;
};

export const EpcBidBondPanel: React.FC<EpcBidBondPanelProps> = ({ tenderId, tender }) => {
  const qc = useQueryClient();
  const meta = (tender.meta_json || tender.metaJson || {}) as Record<string, unknown>;
  const bond = parseBidBond(meta.bid_bond);
  const readiness = buildBidBondReadiness(meta.bid_bond);
  const [draft, setDraft] = useState(bond);

  const saveM = useMutation({
    mutationFn: () =>
      epcCommercialService.patchTender(tenderId, {
        metaJson: {
          ...meta,
          bid_bond: {
            ...draft,
            required: draft.status !== 'NOT_REQUIRED',
          },
        },
      }),
    onSuccess: () => {
      message.success('Bid bond updated');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  return (
    <Card size="small" title="Bid bond" id="bid-bond">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Tag color={readiness.ready ? 'green' : 'orange'}>{readiness.status.replace(/_/g, ' ')}</Tag>
        <div>
          <Text type="secondary">Required</Text>
          <Select
            style={{ width: '100%', marginTop: 4 }}
            value={draft.status}
            options={BID_BOND_STATUS_OPTIONS}
            onChange={(status: BidBondStatus) => setDraft({ ...draft, status, required: status !== 'NOT_REQUIRED' })}
          />
        </div>
        <div>
          <Text type="secondary">Amount</Text>
          <InputNumber
            style={{ width: '100%', marginTop: 4 }}
            min={0}
            value={draft.amount}
            onChange={(n) => setDraft({ ...draft, amount: n == null ? undefined : Number(n) })}
          />
          {draft.amount != null && (
            <Text type="secondary"> {formatMoney(draft.amount, { currency: draft.currency || 'AED' })}</Text>
          )}
        </div>
        <Button type="primary" loading={saveM.isPending} onClick={() => saveM.mutate()}>
          Save bid bond
        </Button>
      </Space>
    </Card>
  );
};

export default EpcBidBondPanel;
